import type { ExportContext } from './exportPlate'
import type { SeriesFrame } from './seriesCatalog'
import type { ToolsView } from './seriesOverride'
import { seriesPlan, type FrameVerdict, type SeriesPlan } from './seriesPlan'
import {
  seriesReportFile, seriesReportText,
  type ExcludedFrame, type ReportFrame, type ReportHeader,
} from './seriesReport'
import { zipStore, type ZipEntry } from './zipStore'

/**
 * Порядок работ серии: разведать → спросить человека → снять → сложить в архив.
 *
 * Страница знает состояние, драйвер знает порядок — то же разделение, что у
 * записи MP4 (`exportVideo.ts` и `exportAnimation` на ModelsPage). Съёмка и
 * сохранение приходят зависимостями: так порядок проверяется без холста и без
 * браузера, а рисованием по-прежнему ведает `exportImage`.
 *
 * Главное правило (владелец, 18.08.2026): картинка, под которой нет данных,
 * наружу не уходит. Проверить её нечем — единиц нет, координат нет, значение
 * это цвет, — и пустое поле на ней неотличимо от честного «здесь ничего нет».
 */

/** Что человек выбрал, увидев план. */
export type SeriesDecision =
  /** Дорисовать недостающие тайлы и дождаться. */
  | 'wait'
  /** Взять только то, что готово сейчас. */
  | 'partial'
  | 'cancel'

/** Что драйвер спрашивает у страницы. */
export interface SeriesRunner {
  /** Есть ли что снимать. */
  ready: boolean
  /** Почему нельзя — для подсказки под кнопкой. */
  reason: 'ok' | 'no-series'
  frames: SeriesFrame[]
  /** Разведка: что со слоем кадра, ещё ничего не рисуя и не трогая карту. */
  probe: (frame: SeriesFrame) => Promise<FrameVerdict>
  /** Дорисовать тайлы кадра и дождаться. `false` — не дождались. */
  render: (frame: SeriesFrame, onProgress?: (share: number) => void) => Promise<boolean>
  /** Войти в режим серии и запомнить пользовательский вид. */
  begin: () => Promise<void>
  /** Показать кадр и дождаться, пока он действительно построится. */
  apply: (frame: SeriesFrame) => Promise<{
    waitedMs: number
    toolsTimedOut: boolean
    /** Плитки слоя не легли на карту за отведённое время. */
    tilesTimedOut: boolean
    tools: ToolsView
    legend: { text: string; note?: string }[]
  }>
  /** Живая плашка ЭТОГО кадра. Спрашивается после `apply`. */
  info: () => ExportContext
  header: () => ReportHeader
  /** Данные кадра одним файлом; отсутствует — выгрузку не просим. */
  dataset?: () => Promise<Uint8Array<ArrayBuffer> | null>
  /** Вернуть пользовательский вид. Зовётся всегда. */
  end: () => void
}

export interface SeriesDeps {
  /** Снять кадр карты в PNG. */
  capture: (info: ExportContext) => Promise<Uint8Array<ArrayBuffer>>
  /** Отдать готовый архив человеку. */
  save: (data: Uint8Array<ArrayBuffer>, fileName: string) => void
  /** Спросить человека, что делать с неготовыми кадрами.
   *
   *  Зовётся ТОЛЬКО когда есть о чём спрашивать: полностью готовая серия
   *  снимается молча, а серия, от которой не осталось ни одного кадра, не
   *  предлагает выбора, которого нет. */
  decide: (plan: SeriesPlan) => Promise<SeriesDecision>
  /** Данные кадра одним файлом NetCDF; null/пусто — не кладём.
   *
   *  Отдельной зависимостью, потому что это ДРУГОЙ разговор: картинки собирает
   *  браузер из холста, а файл данных собирает сервер из базы. Его отказ
   *  (слишком широкий кадр, обрыв сети) не имеет права отменить готовые карты. */
  dataset?: () => Promise<Uint8Array<ArrayBuffer> | null>
  now?: () => Date
  onProgress?: (share: number) => void
  /** Какой кадр снимается сейчас — для подписи на кнопке. */
  onFrame?: (index: number, labelKey: string) => void
}

export type SeriesResult =
  | 'saved'
  | 'no-series'
  | 'failed'
  /** Человек отказался от неполной серии. */
  | 'cancelled'
  /** Отдавать было нечего: под кадрами нет данных. */
  | 'nothing'

/** Папка внутри архива — имя самого архива без расширения: распакованные файлы
 *  не рассыпаются по загрузкам, а лежат кучкой и в понятном порядке. */
function folderOf(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '')
}

export async function runSeries(
  runner: SeriesRunner,
  fileName: string,
  deps: SeriesDeps,
): Promise<SeriesResult> {
  if (!runner.ready || runner.frames.length === 0) return 'no-series'
  const total = runner.frames.length
  const folder = folderOf(fileName)
  const progress = (share: number) => deps.onProgress?.(Math.min(1, Math.max(0, share)))

  try {
    // ── Разведка. Идёт по ВСЕМ кадрам до первой съёмки: вопрос человеку
    // задаётся один раз и со всей картиной сразу. Спрашивая «ждать ли?»
    // посреди третьего кадра, мы спросили бы четырежды.
    const states = []
    for (const frame of runner.frames) {
      let verdict: FrameVerdict = 'unknown'
      try {
        verdict = await runner.probe(frame)
      } catch {
        // Молчание — не готовность: доказательства данных у нас нет.
        verdict = 'unknown'
      }
      states.push({ frame, verdict })
    }
    const plan = seriesPlan(states)
    if (plan.choice === 'nothing') return 'nothing'

    const decision: SeriesDecision = plan.choice === 'go' ? 'wait' : await deps.decide(plan)
    if (decision === 'cancel') return 'cancelled'

    const chosen = new Set(plan.ready.map(f => f.id))
    if (decision === 'wait') plan.renderable.forEach(f => chosen.add(f.id))

    const excluded: ExcludedFrame[] = states
      .filter(s => !chosen.has(s.frame.id))
      .map(s => ({
        frame: s.frame,
        // Кадр, который МОЖНО было дорисовать, но человек выбрал готовые, —
        // это его решение, а не пропажа данных. Назвать их одним словом
        // значило бы соврать про источник.
        reason: s.verdict === 'needs-render'
          ? ('skipped' as const)
          : s.verdict === 'no-data'
            ? ('no-data' as const)
            : ('unknown' as const),
      }))

    await runner.begin()
    const entries: ZipEntry[] = []
    const report: ReportFrame[] = []

    for (let i = 0; i < total; i++) {
      const frame = runner.frames[i]
      if (!chosen.has(frame.id)) continue
      deps.onFrame?.(i + 1, frame.labelKey)

      // Дорисовка — только тем, у кого поле есть. Полный пайплайн стоит
      // 44 минуты на дату, и нажатие кнопки не может его стоить.
      const needsRender = plan.renderable.some(f => f.id === frame.id)
      if (needsRender) {
        let done = false
        try {
          done = await runner.render(frame, s => progress((i + s * 0.5) / total))
        } catch {
          done = false
        }
        if (!done) {
          // Не дождались — кадра в архиве не будет: тайлы серые, и картинка
          // солгала бы пустотой там, где поле есть.
          excluded.push({ frame, reason: 'render-failed' })
          continue
        }
      }

      const applied = await runner.apply(frame)
      progress((i + 0.9) / total)

      if (applied.tilesTimedOut) {
        excluded.push({ frame, reason: 'tiles-late' })
        continue
      }

      // Плашку спрашиваем ПОСЛЕ применения: иначе она отстанет на кадр и
      // подпишет предыдущий слой поверх нового поля.
      const info = runner.info()
      // Номер кадра — из РЕЕСТРА, а не порядковый: пропуск в нумерации это
      // сообщение («третьего кадра нет»), а перенумеровав, мы выдали бы
      // неполный набор за полный.
      const file = `${i + 1}_${frame.id}.png`
      entries.push({ name: `${folder}/${file}`, data: await deps.capture(info) })
      report.push({
        index: i + 1,
        total,
        file,
        frame,
        tools: applied.tools,
        legend: applied.legend,
        readiness: needsRender ? 'rendered' : 'tiles-ready',
        waitedMs: applied.waitedMs,
        toolsTimedOut: applied.toolsTimedOut,
      })
    }

    if (entries.length === 0) return 'nothing'

    // Данные вьюпорта: те же поля, что на картинках, но числами — с единицами,
    // координатами и всеми горизонтами. Картинку не проверить ничем, файл —
    // можно (см. src/cabinet/grid_export.py на сервере).
    let dataset: 'saved' | 'failed' | 'off' = 'off'
    if (deps.dataset) {
      dataset = 'failed'
      try {
        const nc = await deps.dataset()
        if (nc && nc.length > 0) {
          entries.push({ name: `${folder}/dannye.nc`, data: nc })
          dataset = 'saved'
        }
      } catch {
        // Здесь молчим, но не в файле параметров: строку про пропажу пишет он.
      }
    }

    entries.push({
      name: `${folder}/parametry.txt`,
      data: seriesReportFile(seriesReportText({ ...runner.header(), dataset }, report, excluded)),
    })
    deps.save(zipStore(entries, deps.now?.() ?? new Date()), fileName)
    progress(1)
    return 'saved'
  } catch {
    return 'failed'
  } finally {
    // Вид пользователя возвращается ВСЕГДА. Обрыв на третьем кадре не имеет
    // права оставить человека на чужой глубине с чужой изотермой.
    runner.end()
  }
}
